import DeleteIcon from '@mui/icons-material/Delete';
import DownloadIcon from '@mui/icons-material/Download';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import LinkOutlinedIcon from '@mui/icons-material/LinkOutlined';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import CloseIcon from '@mui/icons-material/Close';
import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  FormControlLabel,
  IconButton,
  Link,
  Radio,
  RadioGroup,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

import { CurrentApplicationContext } from '@/Contexts/CurrentApplicationContext';
import { LocaleContext } from '@/Contexts/LocaleContext';
import { SnackbarContext } from '@/Contexts/SnackbarProvider';
import ConfirmationModal from '@/components/common/ConfirmationModal';
import useApplication from '@/hooks/useApplication';
import useAttachment from '@/hooks/useAttachment';
import useQuestionResponse from '@/hooks/useQuestionResponse';
import useQuestions from '@/hooks/useQuestions';
import { DataUploadFileSection } from '@/components/application_components/question_types/data_upload/DataUploadFileSection';
import { DataUploadUrlSection } from '@/components/application_components/question_types/data_upload/DataUploadUrlSection';
import {
  convertToFileName,
  getComparableFileName,
  getFileExtension,
  getOfficeViewerUrl,
  OFFICE_OPEN_EXTENSIONS,
  OPENABLE_FILE_EXTENSIONS,
  PREVIEW_OPEN_EXTENSIONS,
} from '@/components/application_components/question_types/data_upload/dataUploadUtils';
import { AttachmentProps, FileType, isFileFileType } from '@/models/Attachment';
import { EventProps } from '@/models/Event';
import { QuestionProps } from '@/models/Question';
import { baseUrl, isUrl, MAX_FILE_SIZE } from '@/utils/constants';
import { logError } from '@/utils/errorHandlerUtil';
import { stringHasValue } from '@/utils/stringUtil';

const MAYOR_PDF_QUESTION_IDS = [2186, 2867] as const;

const enum DataUploadRadioOptions {
  file = 1,
  url = 2,
}

type UploadQueueItem = {
  id: string;
  file: File;
  status: 'uploading' | 'failed';
  error?: string;
  retryable: boolean;
};

function createUploadQueueId() {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

function DataUpload({
  question,
  fileObject,
  dataWrapUploadFileCheck,
  eventInfo,
  onPendingUploadsChange,
  disabled,
}: {
  question: QuestionProps;
  fileObject?: AttachmentProps;
  dataWrapUploadFileCheck?: (file: boolean) => void;
  eventInfo?: EventProps | null;
  onPendingUploadsChange?: (hasPending: boolean) => void;
  disabled?: boolean;
}) {
  const theme = useTheme();
  const textRef = useRef<HTMLInputElement>(null);
  const { deleteFile, getDownloadFile } = useQuestions();
  const { setAlert } = useContext(SnackbarContext);

  const { getQuestionAnswer } = useQuestionResponse();
  const { getSummary } = useApplication();
  const { answers, currentApplication, canEdit, saveApplicationAnswer, updateAnswers } =
    useContext(CurrentApplicationContext);
  const { postUrlAttachment, postUploadedData } = useAttachment();

  const [file, setFile] = useState<string>();
  const [uploadedFileName, setUploadedFileName] = useState<string | null>(null);
  const [fileName, setFileName] = useState<string>('');
  const [urlLink, setUrlLink] = useState<string>('');
  const [urlFile, setUrlFile] = useState<string | null>(null);
  const [fileType, setFileType] = useState<DataUploadRadioOptions>(DataUploadRadioOptions.file);

  const [errorMessage, setErrorMessage] = useState<string>('');
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [uploadQueue, setUploadQueue] = useState<UploadQueueItem[]>([]);

  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);

  const [loadingPdf, setLoadingPdf] = useState<boolean>(true);

  const { locale, translatedText } = useContext(LocaleContext);
  const pendingUploadsChangeRef = useRef(onPendingUploadsChange);

  const questionAnswer = useMemo(() => answers[question.id], [answers, question.id]);
  const filesForCurrentQuestion = useMemo(() => {
    const files = questionAnswer?.files ?? [];
    if (question.questionType === 'event' && eventInfo?.id != null) {
      return files.filter((entry: AttachmentProps) => entry.eventId === eventInfo.id);
    }

    return files;
  }, [eventInfo?.id, question.questionType, questionAnswer?.files]);

  const existingFileNameSet = useMemo(() => {
    return new Set(
      filesForCurrentQuestion
        .filter((entry: AttachmentProps) => {
          if (entry.fileType == null) return false;
          return isFileFileType(entry.fileType);
        })
        .map((entry: AttachmentProps) => getComparableFileName(entry.fileName ?? ''))
        .filter((name) => name !== '')
    );
  }, [filesForCurrentQuestion]);

  const existingUrlSet = useMemo(() => {
    return new Set(
      filesForCurrentQuestion
        .filter((entry: AttachmentProps) => entry.fileType === FileType.url)
        .map((entry: AttachmentProps) => (entry.uri ?? '').trim().toLowerCase())
        .filter((url) => url !== '')
    );
  }, [filesForCurrentQuestion]);

  const isMayorPdfQuestion = useMemo(
    () => MAYOR_PDF_QUESTION_IDS.includes(question.id as (typeof MAYOR_PDF_QUESTION_IDS)[number]),
    [question.id]
  );
  const hasUploadedFile = uploadedFileName != null && uploadedFileName !== '';
  const showUploadForm = !loading && !hasUploadedFile && !isMayorPdfQuestion;
  const uploadedFileDisplayName =
    fileName !== '' && fileName != null
      ? fileName
      : uploadedFileName != null
        ? convertToFileName(uploadedFileName)
        : '';
  const isUploadedFileType = fileType === DataUploadRadioOptions.file;
  const uploadedFileExtension = getFileExtension(fileObject?.uri ?? uploadedFileName ?? '');
  const canOpenUploadedFile =
    isUploadedFileType &&
    uploadedFileExtension != null &&
    OPENABLE_FILE_EXTENSIONS.includes(
      uploadedFileExtension as (typeof OPENABLE_FILE_EXTENSIONS)[number]
    );
  const canShowOpenAction = !isUploadedFileType || canOpenUploadedFile;

  const handleChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    const isFile = value === DataUploadRadioOptions.file.toString();
    setFileType(isFile ? DataUploadRadioOptions.file : DataUploadRadioOptions.url);
  }, []);

  useEffect(() => {
    let url: string;

    const fetchData = async () => {
      const fileData = await getSummary(currentApplication.id);
      const fi = new Blob([fileData], { type: 'application/pdf' });
      url = URL.createObjectURL(fi);
      setFile(url);
      setLoadingPdf(false);
    };

    if (isMayorPdfQuestion) {
      // mayor pdf download
      void fetchData();
    }

    return () => {
      if (url != null) URL.revokeObjectURL(url);
    };
  }, [currentApplication.id, getSummary, isMayorPdfQuestion]);

  useEffect(() => {
    if (fileObject?.fileType == null) {
      setUploadedFileName(null);
      setFileName('');
      setUrlLink('');
      setUrlFile(null);
      setFileType(DataUploadRadioOptions.file);
      setLoading(false);
      return;
    }

    setFileType(
      isFileFileType(fileObject.fileType) ? DataUploadRadioOptions.file : DataUploadRadioOptions.url
    );

    if (isFileFileType(fileObject.fileType) && stringHasValue(fileObject.fileName)) {
      const updatedFileName: string = fileObject.fileName;
      setFileName(convertToFileName(updatedFileName));
      setUploadedFileName(updatedFileName);
      setUrlLink('');
      setUrlFile(null);
    } else if (fileObject.fileType === FileType.url) {
      const url = fileObject.uri;
      setUrlLink(url ?? '');
      setUrlFile(fileObject.fileName ?? '');
      setUploadedFileName(url ?? null);
      setFileName('');
    } else {
      setUploadedFileName(null);
      setFileName('');
      setUrlLink('');
      setUrlFile(null);
    }

    setLoading(false);
  }, [fileObject?.fileType, fileObject?.fileName, fileObject?.uri]);

  useEffect(() => {
    pendingUploadsChangeRef.current = onPendingUploadsChange;
  }, [onPendingUploadsChange]);

  useEffect(() => {
    pendingUploadsChangeRef.current?.(uploadQueue.length > 0);
  }, [uploadQueue.length]);

  useEffect(() => {
    return () => {
      pendingUploadsChangeRef.current?.(false);
    };
  }, []);

  const updateApplicationAnswer = useCallback(async () => {
    if (question.questionType !== 'event' && locale != null) {
      await saveApplicationAnswer(
        currentApplication.id,
        question.id,
        'completed',
        undefined,
        locale
      );
    } else if (question.questionType === 'event') {
      const updatedAnswer = await getQuestionAnswer(question.id, currentApplication.id);
      if (updatedAnswer != null) updateAnswers(updatedAnswer);
    }
  }, [
    currentApplication.id,
    getQuestionAnswer,
    locale,
    question.id,
    question.questionType,
    saveApplicationAnswer,
    updateAnswers,
  ]);

  const postUrl = useCallback(
    async (url: string) => {
      setUploadedFileName(url);
      setLoading(true);

      const eventId =
        question.questionType === 'event' && eventInfo?.id != null ? eventInfo.id : undefined;

      try {
        await postUrlAttachment(question.id, url, eventId);
        await updateApplicationAnswer();

        dataWrapUploadFileCheck?.(true);

        setFileType(DataUploadRadioOptions.url);
        setUrlLink(url);
        setUrlFile(url);
        setUploadedFileName(url);

        setAlert({
          severity: 'success',
          message: translatedText.dataUpload.messageSuccess,
        });
      } catch (error: unknown) {
        if (questionAnswer != null && questionAnswer.files.length <= 1 && locale != null) {
          await saveApplicationAnswer(currentApplication.id, question.id, null, undefined, locale);
          dataWrapUploadFileCheck?.(false);
        }

        setUploadedFileName(null);
        setAlert({
          severity: 'error',
          message: translatedText.dataUpload.messageFailed,
        });
        setErrorMessage(translatedText.dataUpload.messageFailed);
        logError('Failed to post url', error);
      } finally {
        setLoading(false);
      }
    },
    [
      currentApplication.id,
      dataWrapUploadFileCheck,
      eventInfo?.id,
      locale,
      postUrlAttachment,
      question.id,
      question.questionType,
      questionAnswer,
      saveApplicationAnswer,
      setAlert,
      translatedText.dataUpload.messageFailed,
      translatedText.dataUpload.messageSuccess,
      updateApplicationAnswer,
    ]
  );

  const handleDelete = useCallback(async () => {
    if (fileType == null) return;

    const uploadedFile = fileType === DataUploadRadioOptions.url ? urlFile : uploadedFileName;

    if (uploadedFile == null && questionAnswer == null) return;

    if (fileObject?.id == null) {
      return;
    }

    setLoading(true);
    try {
      await deleteFile(fileObject.id);

      let answerFiles = questionAnswer?.files ?? [];
      if (question.questionType === 'event' && eventInfo != null) {
        answerFiles = answerFiles.filter((f: AttachmentProps) => f.eventId === eventInfo.id);
      }

      const remainingFiles = answerFiles.filter(
        (f: AttachmentProps) => f.fileName !== uploadedFile
      );

      if (locale != null) {
        const answerStatus = remainingFiles.length < 1 ? null : 'completed';
        await saveApplicationAnswer(
          currentApplication.id,
          question.id,
          answerStatus,
          undefined,
          locale
        );

        if (answerStatus === null) {
          dataWrapUploadFileCheck?.(false);
        }
      }
    } finally {
      setLoading(false);
      setFileName('');
      setUploadedFileName(null);
      setUrlLink('');
      setUrlFile(null);
    }
  }, [
    currentApplication.id,
    dataWrapUploadFileCheck,
    deleteFile,
    eventInfo,
    fileObject?.id,
    fileType,
    locale,
    question.id,
    question.questionType,
    questionAnswer,
    saveApplicationAnswer,
    uploadedFileName,
    urlFile,
  ]);

  const openDeleteConfirmation = useCallback(() => {
    setConfirmDeleteOpen(true);
  }, []);

  const closeDeleteConfirmation = useCallback(() => {
    setConfirmDeleteOpen(false);
  }, []);

  const postFile = useCallback(
    async (selectedFile: File | null, uploadFileName: string) => {
      if (selectedFile == null || locale == null || question.id == null) return false;

      setErrorMessage('');

      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('fileName', uploadFileName);

      const eventId = question.questionType === 'event' ? eventInfo?.id : undefined;

      try {
        await postUploadedData(question.id, formData, null, eventId);

        await updateApplicationAnswer();

        dataWrapUploadFileCheck?.(true);

        setAlert({
          severity: 'success',
          message: translatedText.dataUpload.messageSuccess,
        });

        return true;
      } catch (error: unknown) {
        logError('Failed to post file', error);
        setAlert({
          severity: 'error',
          message: translatedText.dataUpload.messageFailed,
        });

        return false;
      }
    },
    [
      dataWrapUploadFileCheck,
      eventInfo?.id,
      locale,
      postUploadedData,
      question.id,
      question.questionType,
      setAlert,
      translatedText.dataUpload.messageFailed,
      translatedText.dataUpload.messageSuccess,
      updateApplicationAnswer,
    ]
  );

  const uploadQueueItem = useCallback(
    async (item: UploadQueueItem) => {
      if (item.file.size > MAX_FILE_SIZE) {
        setUploadQueue((prev) =>
          prev.map((queued) =>
            queued.id === item.id
              ? {
                  ...queued,
                  status: 'failed',
                  error: translatedText.dataUpload.errorFileSize,
                  retryable: false,
                }
              : queued
          )
        );
        return;
      }

      const success = await postFile(item.file, item.file.name);

      if (success) {
        setUploadQueue((prev) => prev.filter((queued) => queued.id !== item.id));
        return;
      }

      setUploadQueue((prev) =>
        prev.map((queued) =>
          queued.id === item.id
            ? {
                ...queued,
                status: 'failed',
                error: translatedText.dataUpload.messageFailed,
              }
            : queued
        )
      );
    },
    [postFile, translatedText.dataUpload.errorFileSize, translatedText.dataUpload.messageFailed]
  );

  const handleFileUpload = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const selectedFiles = Array.from(e.target.files ?? []);

      if (selectedFiles.length === 0) {
        return;
      }

      const unsupportedFiles = selectedFiles.filter(
        (selectedFile) => getFileExtension(selectedFile.name) === 'zip'
      );

      const uploadableFiles = selectedFiles.filter(
        (selectedFile) => getFileExtension(selectedFile.name) !== 'zip'
      );

      const validFiles = uploadableFiles.filter(
        (selectedFile) => selectedFile.size <= MAX_FILE_SIZE
      );
      const invalidFiles = uploadableFiles.filter(
        (selectedFile) => selectedFile.size > MAX_FILE_SIZE
      );

      const seenSelectedNames = new Set<string>();
      const dedupedValidFiles: File[] = [];
      let duplicateFileCount = 0;

      for (const selectedFile of validFiles) {
        const comparableName = getComparableFileName(selectedFile.name);
        const isDuplicate =
          seenSelectedNames.has(comparableName) || existingFileNameSet.has(comparableName);

        if (isDuplicate) {
          duplicateFileCount += 1;
          continue;
        }

        seenSelectedNames.add(comparableName);
        dedupedValidFiles.push(selectedFile);
      }

      if (unsupportedFiles.length > 0) {
        const unsupportedItems: UploadQueueItem[] = unsupportedFiles.map((selectedFile) => ({
          id: createUploadQueueId(),
          file: selectedFile,
          status: 'failed',
          retryable: false,
          error: String(translatedText.dataUpload.errorFileTypeUnsupported),
        }));
        setUploadQueue((prev) => [...prev, ...unsupportedItems]);
      }

      if (invalidFiles.length > 0) {
        const invalidItems: UploadQueueItem[] = invalidFiles.map((selectedFile) => ({
          id: createUploadQueueId(),
          file: selectedFile,
          status: 'failed',
          retryable: false,
          error: translatedText.dataUpload.errorFileSize,
        }));
        setUploadQueue((prev) => [...prev, ...invalidItems]);
      }

      if (unsupportedFiles.length > 0) {
        setAlert({
          severity: 'error',
          message: String(translatedText.dataUpload.errorFileTypeUnsupported),
        });
      }

      if (invalidFiles.length > 0) {
        setAlert({
          severity: 'error',
          message: translatedText.dataUpload.errorFileSize,
        });
      }

      if (duplicateFileCount > 0) {
        setAlert({
          severity: 'warning',
          message: String(translatedText.dataUpload.warningDuplicateFileSkipped),
        });
      }

      const newQueueItems: UploadQueueItem[] = dedupedValidFiles.map((selectedFile) => ({
        id: createUploadQueueId(),
        file: selectedFile,
        status: 'uploading',
        retryable: true,
      }));

      setUploadQueue((prev) => [...prev, ...newQueueItems]);

      for (const queueItem of newQueueItems) {
        void uploadQueueItem(queueItem);
      }

      e.target.value = '';
    },
    [
      existingFileNameSet,
      setAlert,
      translatedText.dataUpload.errorFileTypeUnsupported,
      translatedText.dataUpload.errorFileSize,
      translatedText.dataUpload.warningDuplicateFileSkipped,
      uploadQueueItem,
    ]
  );

  const handleOpenAttachment = useCallback(
    async (fileKey: string) => {
      setDownloading(true);

      const extension = getFileExtension(fileKey);
      const canOpenFileType =
        extension != null &&
        OPENABLE_FILE_EXTENSIONS.includes(extension as (typeof OPENABLE_FILE_EXTENSIONS)[number]);

      if (!canOpenFileType) {
        setDownloading(false);
        return;
      }

      const openInOfficeViewer = OFFICE_OPEN_EXTENSIONS.includes(
        extension as (typeof OFFICE_OPEN_EXTENSIONS)[number]
      );
      const openViaBlobPreview = PREVIEW_OPEN_EXTENSIONS.includes(
        extension as (typeof PREVIEW_OPEN_EXTENSIONS)[number]
      );

      const fileUri = fileObject?.uri ?? null;

      if (fileUri != null && !openViaBlobPreview) {
        if (openInOfficeViewer) {
          const officeViewerUrl = getOfficeViewerUrl(fileUri);
          window.open(officeViewerUrl, '_blank', 'noopener,noreferrer');
        } else {
          window.open(fileUri, '_blank', 'noopener,noreferrer');
        }
        setDownloading(false);
        return;
      }

      if (openInOfficeViewer) {
        const downloadUrl = `${baseUrl}files/download?filename=${encodeURIComponent(fileKey)}`;
        const officeViewerUrl = getOfficeViewerUrl(downloadUrl);

        window.open(officeViewerUrl, '_blank', 'noopener,noreferrer');
        setDownloading(false);
        return;
      }

      try {
        const results = await getDownloadFile(fileKey);
        if (results.response.status === 200) {
          const contentType = results.contentType;
          const blob = new Blob([results.blob], { type: contentType ?? undefined });
          const objectUrl = URL.createObjectURL(blob);

          window.open(objectUrl, '_blank', 'noopener,noreferrer');
          setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
        }
      } catch (error: unknown) {
        logError('Failed to open attachment', error);
      } finally {
        setDownloading(false);
      }
    },
    [fileObject?.uri, getDownloadFile]
  );

  const handleDownloadAttachment = useCallback(
    async (fileKey: string) => {
      setDownloading(true);

      try {
        const results = await getDownloadFile(fileKey);

        if (results.response.status === 200) {
          const contentType = results.contentType;
          const blob = new Blob([results.blob], { type: contentType ?? undefined });
          const objectUrl = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = objectUrl;
          link.download = uploadedFileDisplayName;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
        }
      } finally {
        setDownloading(false);
      }
    },
    [getDownloadFile, uploadedFileDisplayName]
  );

  const handleSaveUrl = useCallback(async () => {
    const urlValue = textRef.current?.value ?? '';
    if (!isUrl(urlValue)) {
      setAlert({
        severity: 'error',
        message: translatedText.dataUpload.errorURL,
      });
      return;
    }

    if (existingUrlSet.has(urlValue.trim().toLowerCase())) {
      setAlert({
        severity: 'warning',
        message: String(translatedText.dataUpload.warningDuplicateUrlExists),
      });
      return;
    }

    await postUrl(urlValue);
  }, [
    existingUrlSet,
    postUrl,
    setAlert,
    translatedText.dataUpload.errorURL,
    translatedText.dataUpload.warningDuplicateUrlExists,
  ]);

  const handleOpenUrlAttachment = useCallback((url: string) => {
    const resolvedUrl = getOfficeViewerUrl(url);
    window.open(resolvedUrl, '_blank', 'noopener,noreferrer');
  }, []);

  return (
    <Box marginTop='10px' display='flex' flexDirection='column' gap='10px' maxWidth='60dvw'>
      {loadingPdf && isMayorPdfQuestion && <CircularProgress />}
      {file != null && !loading && (
        <iframe
          width='800px'
          height='800px'
          style={{ display: 'flex', justifySelf: 'center', alignSelf: 'center' }}
          src={file}
          title='title'
        >
          <a href={file}>{translatedText.dataUpload.messageDownload}</a>
        </iframe>
      )}

      {showUploadForm ? (
        <form
          name='form1'
          id='myForm'
          encType='multipart/form-data'
          onSubmit={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
        >
          <FormControl disabled={disabled}>
            <RadioGroup
              row
              aria-labelledby='demo-row-radio-buttons-group-label'
              name='row-radio-buttons-group'
              value={fileType}
              onChange={handleChange}
            >
              <FormControlLabel
                value={DataUploadRadioOptions.file}
                control={<Radio />}
                label={translatedText.dataUpload.labelFile}
              />
              <FormControlLabel
                value={DataUploadRadioOptions.url}
                control={<Radio />}
                label={translatedText.dataUpload.labelURL}
              />
            </RadioGroup>
          </FormControl>

          {fileType === DataUploadRadioOptions.file ? (
            <DataUploadFileSection
              questionId={question.id}
              fileName={fileName}
              disabled={disabled}
              canEdit={canEdit}
              onFileUpload={handleFileUpload}
              selectFileLabel={translatedText.dataUpload.labelSelectFile}
              helperText={translatedText.dataUpload.helperSelectFile}
            />
          ) : (
            <DataUploadUrlSection
              inputRef={textRef}
              urlLink={urlLink}
              disabled={disabled}
              placeholder={translatedText.dataUpload.placeholderURL}
              saveLabel={translatedText.dataUpload.labelSave}
              helperText={translatedText.dataUpload.helperUrl}
              onSave={handleSaveUrl}
            />
          )}

          {stringHasValue(errorMessage) && uploadQueue.length === 0 && (
            <Box sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
              <Typography variant='subtitle3' color='error'>
                {errorMessage}
              </Typography>
            </Box>
          )}

          {uploadQueue.length > 0 && (
            <Box
              sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 1, maxWidth: '550px' }}
            >
              {uploadQueue.map((item) => (
                <Stack
                  key={item.id}
                  direction='row'
                  alignItems='center'
                  justifyContent='space-between'
                  sx={{
                    border: '1px solid',
                    borderColor: item.status === 'failed' ? 'error.light' : 'divider',
                    borderRadius: 1,
                    px: 1,
                    py: 0.75,
                  }}
                >
                  <Box sx={{ minWidth: 0 }}>
                    <Typography sx={{ wordBreak: 'break-word' }}>{item.file.name}</Typography>
                    {item.status === 'failed' && item.error != null && (
                      <Typography variant='subtitle3' color='error'>
                        {item.error}
                      </Typography>
                    )}
                  </Box>

                  {item.status === 'uploading' ? (
                    <CircularProgress size='16px' />
                  ) : (
                    <Stack direction='row' alignItems='center' gap='4px'>
                      {item.retryable && (
                        <Button
                          variant='secondary'
                          size='small'
                          onClick={() => {
                            setUploadQueue((prev) =>
                              prev.map((queued) =>
                                queued.id === item.id
                                  ? { ...queued, status: 'uploading', error: undefined }
                                  : queued
                              )
                            );
                            void uploadQueueItem({
                              ...item,
                              status: 'uploading',
                              error: undefined,
                            });
                          }}
                        >
                          {String(translatedText.dataUpload.labelTryAgain)}
                        </Button>
                      )}

                      <Tooltip title={String(translatedText.dataUpload.tooltipClearError)}>
                        <IconButton
                          size='small'
                          aria-label={String(translatedText.dataUpload.ariaClearUploadError)}
                          onClick={() =>
                            setUploadQueue((prev) => prev.filter((queued) => queued.id !== item.id))
                          }
                        >
                          <CloseIcon fontSize='small' sx={{ color: theme.palette.taiga.main }} />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  )}
                </Stack>
              ))}
            </Box>
          )}
        </form>
      ) : (
        <></>
      )}

      {stringHasValue(uploadedFileName) && !loading && (
        <Box
          sx={{
            border: '1px solid',
            borderColor: isUploadedFileType ? 'divider' : 'info.light',
            borderRadius: '10px',
            padding: '8px 12px',
            backgroundColor: 'grey.50',
          }}
        >
          <Stack direction='row' alignItems='center' justifyContent='space-between' mb='4px'>
            <Stack direction='row' alignItems='center' gap='6px' minWidth={0}>
              {isUploadedFileType ? (
                <InsertDriveFileOutlinedIcon fontSize='small' sx={{ color: 'text.secondary' }} />
              ) : (
                <LinkOutlinedIcon fontSize='small' sx={{ color: 'info.main' }} />
              )}
              <Typography variant='subtitle3' color='textSecondary'>
                {isUploadedFileType
                  ? String(translatedText.dataUpload.labelUploadedFile)
                  : String(translatedText.dataUpload.labelSavedUrl)}
              </Typography>
            </Stack>

            {uploadedFileName != null && (
              <Stack direction='row' alignItems='center' gap='4px'>
                {canShowOpenAction && (
                  <Tooltip title={String(translatedText.dataUpload.tooltipOpen)}>
                    <span>
                      <IconButton
                        aria-label={
                          isUploadedFileType
                            ? String(translatedText.dataUpload.ariaOpenAttachment)
                            : String(translatedText.dataUpload.ariaOpenUrlAttachment)
                        }
                        onClick={() =>
                          isUploadedFileType
                            ? void handleOpenAttachment(uploadedFileName)
                            : handleOpenUrlAttachment(uploadedFileName)
                        }
                        disabled={isUploadedFileType && downloading}
                        size='small'
                      >
                        <OpenInNewIcon fontSize='small' sx={{ color: theme.palette.taiga.main }} />
                      </IconButton>
                    </span>
                  </Tooltip>
                )}

                {isUploadedFileType && (
                  <Tooltip title={String(translatedText.dataUpload.tooltipDownload)}>
                    <span>
                      <IconButton
                        aria-label={String(translatedText.dataUpload.ariaDownloadAttachment)}
                        onClick={() => void handleDownloadAttachment(uploadedFileName)}
                        disabled={downloading}
                        size='small'
                      >
                        <DownloadIcon fontSize='small' sx={{ color: theme.palette.taiga.main }} />
                      </IconButton>
                    </span>
                  </Tooltip>
                )}

                {canEdit && (
                  <Tooltip title={String(translatedText.dataUpload.tooltipDelete)}>
                    <IconButton onClick={openDeleteConfirmation} size='small'>
                      <DeleteIcon fontSize='small' sx={{ color: theme.palette.taiga.main }} />
                    </IconButton>
                  </Tooltip>
                )}

                {downloading && <CircularProgress size='16px' />}
              </Stack>
            )}
          </Stack>

          <Box sx={{ width: '100%', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {isUploadedFileType ? (
              <Typography sx={{ wordBreak: 'break-word' }}>{uploadedFileDisplayName}</Typography>
            ) : (
              <Link
                href={uploadedFileName != null ? getOfficeViewerUrl(uploadedFileName) : undefined}
                target='_blank'
                rel='noopener noreferrer'
                sx={{ wordBreak: 'break-all' }}
              >
                {uploadedFileName}
              </Link>
            )}
          </Box>
        </Box>
      )}

      <ConfirmationModal
        open={confirmDeleteOpen}
        onClose={closeDeleteConfirmation}
        header={translatedText.dataUpload.confirmationModal.header}
        confirmationText={translatedText.dataUpload.confirmationModal.confirmationMessage}
        confirmButtonText={translatedText.dataUpload.confirmationModal.confirmationButtonText}
        onDelete={handleDelete}
      />

      {loading && (
        <Box
          display='flex'
          alignItems='center'
          justifyContent='flex-start'
          gap='15px'
          margin='10px 0px'
          sx={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          <Typography>{uploadedFileName}</Typography>
          <CircularProgress size='16px' />
        </Box>
      )}
    </Box>
  );
}

export default DataUpload;
